class CartRecommendationsManager {
  constructor() {
    this.currentRequest = null;
    this.swiper = null;
    this.debounceTimer = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail);
    });

    document.addEventListener('cart-drawer:opened', () => {
      this.loadRecommendationsForLastCartItem();
    });

    document.addEventListener('product:added-to-cart', (event) => {
      this.handleProductAdded(event.detail);
    });
  }

  handleCartUpdate(cartData) {
    if (cartData.item_count === 0) {
      this.hideRecommendations();
    } else {
      setTimeout(() => {
        this.reinitializeSwiper();
      }, 200);
    }
  }

  handleProductAdded(productData) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.loadRecommendationsForLastCartItem();
      setTimeout(() => {
        this.reinitializeSwiper();
      }, 100);
    }, 500);
  }

  loadRecommendationsForLastCartItem() {
    const recommendationsContainer = document.getElementById('cart-recommendations');
    if (!recommendationsContainer) return;

    const cartItems = document.querySelectorAll('.cart-item');
    if (cartItems.length === 0) return this.hideRecommendations();

    const lastCartItem = cartItems[cartItems.length - 1];
    const productLink = lastCartItem.querySelector('a[href*="/products/"]');
    if (!productLink) return this.hideRecommendations();

    const productHandle = this.extractProductHandle(productLink.href);
    productHandle ? this.loadRecommendations(productHandle) : this.hideRecommendations();
  }

  extractProductHandle(productUrl) {
    try {
      const handle = productUrl.split('/products/')[1];
      return handle ? handle.split('?')[0].split('&')[0] : null;
    } catch (error) {
      console.error('Error extracting product handle:', error);
      return null;
    }
  }

  async loadRecommendations(productHandle) {
    const recommendationsContainer = document.getElementById('cart-recommendations');
    const recommendationsGrid = document.getElementById('recommendations-grid');
    
    if (!recommendationsContainer || !recommendationsGrid) return;

    if (this.currentRequest === productHandle) return;
    
    this.currentRequest = productHandle;
    this.showLoadingState();

    try {
      const productResponse = await fetch(`/products/${productHandle}.js`);
      if (!productResponse.ok) throw new Error(`HTTP error! status: ${productResponse.status}`);
      
      const product = await productResponse.json();
      
      const recommendationsResponse = await fetch(
        `${window.location.origin}/recommendations/products?product_id=${product.id}&limit=5&section_id=cart-drawer-recommendations`
      );
      
      if (!recommendationsResponse.ok) throw new Error(`HTTP error! status: ${recommendationsResponse.status}`);
      
      const text = await recommendationsResponse.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('.cart-drawer__recommendations');

      recommendations?.innerHTML.trim().length ? this.renderRecommendations(recommendations) : this.showEmptyState();
    } catch (error) {
      console.error('Error loading cart recommendations:', error);
      this.showErrorState();
    } finally {
      this.currentRequest = null;
    }
  }

  showLoadingState() {
    const container = document.getElementById('cart-recommendations');
    const grid = document.getElementById('recommendations-grid');
    if (!container || !grid) return;

    container.style.display = 'block';
    grid.innerHTML = '<div class="cart-recommendations-loading"><div class="loading__spinner"></div></div>';
  }

  showEmptyState() {
    const container = document.getElementById('cart-recommendations');
    const grid = document.getElementById('recommendations-grid');
    if (!container || !grid) return;

    container.style.display = 'block';
    grid.innerHTML = '<div class="cart-recommendations-empty">No recommendations available at the moment.</div>';
  }

  showErrorState() {
    const container = document.getElementById('cart-recommendations');
    if (container) container.style.display = 'none';
  }

  hideRecommendations() {
    const container = document.getElementById('cart-recommendations');
    if (container) container.style.display = 'none';
  }

  renderRecommendations(recommendationsElement) {
    const container = document.getElementById('cart-recommendations');
    const grid = document.getElementById('recommendations-grid');
    if (!container || !grid) return;

    const swiperElement = recommendationsElement.querySelector('.cart-recommendations-swiper');
    if (!swiperElement) return this.showEmptyState();

    grid.innerHTML = '';
    const clonedSwiper = swiperElement.cloneNode(true);
    grid.appendChild(clonedSwiper);

    this.initSwiper(clonedSwiper);
    container.style.display = 'block';
  }

  initSwiper(swiperElement) {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (typeof Swiper === 'undefined') {
      this.fallbackToStaticGrid(swiperElement);
      return;
    }

    try {
      this.swiper = new Swiper(swiperElement, {
        slidesPerView: 1.1,
        spaceBetween: 14,
        loop: false,
        grabCursor: true,
        keyboard: { enabled: true },
        slidesOffsetAfter: 20,
        breakpoints: {
          429: {
            slidesPerView: 1.2,
          },
          749: {
            slidesPerView: 1.3,
          }
        }
      });
    } catch (error) {
      console.error('Error initializing Swiper:', error);
      this.fallbackToStaticGrid(swiperElement);
    }
  }

  fallbackToStaticGrid(swiperElement) {
    const wrapper = swiperElement.querySelector('.swiper-wrapper');
    if (wrapper) {
      const slides = wrapper.querySelectorAll('.swiper-slide');
      slides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        slide.style.width = 'auto';
      });
      
      wrapper.classList.remove('swiper-wrapper');
      wrapper.style.display = 'grid';
      wrapper.style.gridTemplateColumns = 'repeat(2, 1fr)';
      wrapper.style.gap = '1rem';
    }

    const navigation = swiperElement.querySelectorAll('.swiper-button-next, .swiper-button-prev, .swiper-pagination');
    navigation.forEach(el => el.style.display = 'none');
  }

  reinitializeSwiper() {
    const swiperElement = document.querySelector('.cart-recommendations-swiper');
    if (swiperElement && this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.initSwiper(swiperElement);
    }
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    this.currentRequest = null;
  }
}

const cartRecommendationsManager = new CartRecommendationsManager();
window.CartRecommendationsManager = cartRecommendationsManager;
